import {
  Database, Cube, ChartLineUp, FileText, MapTrifold, Gauge, Cpu, ShieldCheck,
  LinkSimple, Stack, Graph, Wrench, Certificate, GlobeStand, ClipboardText,
  UsersFour, Waveform, PresentationChart, MonitorPlay, FunnelSimple, Lock,
} from "@phosphor-icons/react";
import { Section, SectionHeading, FeatureCard, CalloutCard } from "@/components/marketing/MarketingUI";
import CockpitHero from "@/components/marketing/CockpitHero";
import useSEO from "@/lib/useSEO";

const CAPABILITIES_COCKPIT = {
  label: "SRES · Capability Matrix",
  kpis: [
    { label: "MODULES",     to: 6,  suffix: "",  delta: "Studio-wide",     deltaColor: "text-slate-500" },
    { label: "CAPABILITIES", to: 40, suffix: "+", delta: "Depth over breadth", deltaColor: "text-brand-blue" },
    { label: "INTEGRATIONS", to: 12, suffix: "+", delta: "GIS · CAD · GSLIB",  deltaColor: "text-slate-500" },
  ],
  variogram: { r2: "0.96", range: "200 m", sill: "0.70", nugget: "0.05" },
  classification: [
    { label: "Measured",  pct: 44 },
    { label: "Indicated", pct: 34 },
    { label: "Inferred",  pct: 22 },
  ],
  status: {
    left: "OK · 3.1 s per 100k blocks",
    right: "40+ capabilities · 6 modules",
  },
};

const GROUPS = [
  {
    name: "Data & QAQC",
    items: [
      { icon: Database, title: "Universal ingest", description: "CSV, XLSX, SHP, DWG readers with forgiving column alias auto-mapping." },
      { icon: ClipboardText, title: "Validation engine", description: "Errors, warnings and infos across collar / survey / assay / geology." },
      { icon: Waveform, title: "CRM / Blank / Dup analytics", description: "Precision, bias, HARD, control-chart envelopes and top-cut suggestions." },
      { icon: FunnelSimple, title: "Domain scripts", description: "Save reusable pandas queries per project and per user." },
    ],
  },
  {
    name: "Geoscience",
    items: [
      { icon: Cube, title: "Block modelling", description: "IDW, NN and Ordinary Kriging with composite lengths and per-domain runs." },
      { icon: Graph, title: "Variography", description: "Experimental and fitted variograms — spherical, exponential, Gaussian." },
      { icon: PresentationChart, title: "Statistics", description: "Histogram, CDF, Q-Q, log-probability plots and per-lithology roll-ups." },
      { icon: MapTrifold, title: "3D visualisation", description: "Plotly WebGL scenes for drillholes, block models, sections and pits." },
    ],
  },
  {
    name: "Economics",
    items: [
      { icon: ChartLineUp, title: "Floating-cone pit", description: "Slope-constrained pit optimisation with per-block dispatch." },
      { icon: Gauge, title: "Cut-off sweeps", description: "Live cut-off × NPV curve with a slider — no re-runs required." },
      { icon: Cpu, title: "Monte-Carlo NPV", description: "Triangular distributions, P10 / P50 / P90 and histogram output." },
      { icon: Wrench, title: "Multi-commodity", description: "Fe, Au, Cu, Ag, Ni, U₃O₈, lithium — with the right units and price basis." },
    ],
  },
  {
    name: "Reporting",
    items: [
      { icon: FileText, title: "CPR PDF + DOCX", description: "JORC / NI 43-101 / SAMREC-aligned assembly with charts and narrative." },
      { icon: Certificate, title: "Signatures", description: "Prepared / Reviewed / Approved sign-off blocks with uploaded images." },
      { icon: LinkSimple, title: "Public share links", description: "Watermarked PDFs with expiry, view tracking and audit logs." },
      { icon: Stack, title: "Per-borehole logs", description: "Industry-standard continuous drillhole log PDFs bundled per project." },
    ],
  },
  {
    name: "Platform",
    items: [
      { icon: UsersFour, title: "Multi-tenant auth", description: "Company scoping, admin roles and audit-ready session logs." },
      { icon: ShieldCheck, title: "Enterprise controls", description: "Password policy, brute-force protection, IP allow-listing (on request)." },
      { icon: GlobeStand, title: "Cloud-native", description: "Zero-install browser experience — deploy your workspace in minutes." },
      { icon: Lock, title: "Data residency", description: "Isolated tenant databases with backup and retention policy support." },
    ],
  },
];

export default function CapabilitiesPage() {
  return (
    <div data-testid="capabilities-page">
      <CockpitHero
        eyebrow="Capabilities"
        title={<>Everything a resource practice needs — organised the way <span className="text-brand-blue">geologists think.</span></>}
        description="SRES's capabilities are grouped into five practice areas that mirror the way modern mining teams actually deliver defensible resource work."
        primaryCta={{ label: "Book a walkthrough", to: "/contact" }}
        secondaryCta={{ label: "Read the workflow", to: "/how-it-works" }}
        cockpit={CAPABILITIES_COCKPIT}
        testid="capabilities-hero"
      />

      <Section className="py-20 md:py-28" testid="capabilities-groups">
        <div className="space-y-16">
          {GROUPS.map((g, gi) => (
            <div key={g.name} data-testid={`capabilities-group-${gi}`}>
              <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <div>
                  <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                    <span className="w-6 h-px bg-brand-blue" />
                    Practice area · {String(gi + 1).padStart(2, "0")}
                  </div>
                  <h2 className="mt-4 font-heading font-black text-3xl md:text-4xl lg:text-[40px] text-slate-900 tracking-[-0.02em] leading-tight">{g.name}</h2>
                </div>
                <div className="text-[13.5px] text-slate-500 max-w-md text-right leading-[1.7]">
                  {gi === 0 && "Ingest, validate and QC — the discipline that saves every resource statement."}
                  {gi === 1 && "The heavy geostatistical maths — kriging, variography and 3D scene composition."}
                  {gi === 2 && "Turn a block model into an economic asset with pit optimisation and sensitivity."}
                  {gi === 3 && "Assemble sign-off-ready deliverables aligned to global reporting standards."}
                  {gi === 4 && "Enterprise-grade access, audit and residency controls under the hood."}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {g.items.map((f, i) => (
                  <FeatureCard key={f.title} {...f} testid={`capabilities-group-${gi}-item-${i}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-28 bg-[#F7F8FB]" testid="capabilities-standards">
        <SectionHeading
          eyebrow="Standards"
          title="Aligned with the reporting codes your regulators care about."
          description="SRES's Competent Person Report templates map cleanly onto the guidance documents cited by resource statements around the world."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          <StandardCard code="JORC" region="Australasia" note="Table 1 supporting documentation and disclosure guidance." />
          <StandardCard code="NI 43-101" region="Canada" note="Instrument-compliant technical report structure for CIM committee." />
          <StandardCard code="SAMREC" region="South Africa" note="Public reporting on exploration results, mineral resources and reserves." />
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="capabilities-cta">
        <CalloutCard
          eyebrow="Compare SRES on your workflow"
          title="Bring your current process to the whiteboard. We will map it on to SRES live."
          description="A 60-minute session with a senior geologist — leave with a written implementation plan."
          cta="Book a mapping session"
          to="/contact"
          testid="capabilities-cta-callout"
        />
      </Section>
    </div>
  );
}

function StandardCard({ code, region, note }) {
  return (
    <div className="p-7 rounded-xl border border-slate-200 bg-white hover:border-slate-900 transition-colors">
      <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
        <span className="w-6 h-px bg-brand-blue" />
        {region}
      </div>
      <div className="mt-4 font-heading font-black text-slate-900 text-3xl tracking-[-0.02em]">{code}</div>
      <p className="mt-4 text-slate-600 text-[14px] leading-[1.7]">{note}</p>
    </div>
  );
}
